import React, { useState, useEffect } from 'react';
import { FaTimes, FaUpload, FaSpinner, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import styles from './CocUploadModal.module.css';
import cocAssistantService from '../../services/cocAssistantService';
import { useAccessToken, useLockBodyScroll } from '../../hooks';
import { useAlert } from '../../context/AlertContext';

/**
 * COC Upload Modal Component
 * 
 * Allows users to upload COC documents and get structured JSON interpretation
 * Currently focused on document upload and JSON response display
 */
const CocUploadModal = ({ isOpen, onClose, preloadedResult = null }) => {
    const { getBackendAccessToken } = useAccessToken();
    const { addProcessingJob } = useAlert();
    
    // Lock body scroll when modal is open
    useLockBodyScroll(isOpen);

    // State
    const [step, setStep] = useState(1); // 1: Upload, 2: Processing, 3: Results
    const [cocText, setCocText] = useState('');
    const [sessionId] = useState(`coc-session-${Date.now()}`);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [clarifications, setClarifications] = useState({});
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [pollingIntervalRef, setPollingIntervalRef] = useState(null);

    /**
     * Reset state when modal opens
     */
    useEffect(() => {
        if (isOpen) {
            // If preloaded result provided, show it directly
            if (preloadedResult) {
                setResult(preloadedResult);
                setStep(3);
                setLoading(false);
            } else {
                // Normal reset for new upload
                setStep(1);
                setCocText('');
                setError(null);
                setResult(null);
                setClarifications({});
                setJobId(null);
                setJobStatus(null);
            }
        }
    }, [isOpen, preloadedResult]);

    /**
     * Cleanup polling on unmount or when modal closes
     */
    useEffect(() => {
        if (!isOpen && pollingIntervalRef) {
            clearInterval(pollingIntervalRef);
            setPollingIntervalRef(null);
        }
        
        return () => {
            if (pollingIntervalRef) {
                clearInterval(pollingIntervalRef);
            }
        };
    }, [isOpen, pollingIntervalRef]);

    /**
     * Poll job status until completion
     */
    const startJobPolling = async (submittedJobId) => {
        const accessToken = await getBackendAccessToken();
        
        const poll = async () => {
            try {
                const statusResponse = await cocAssistantService.getJobStatus(accessToken, submittedJobId);
                setJobStatus(statusResponse.status);

                if (statusResponse.status === 'COMPLETED') {
                    // Job completed - fetch result
                    if (pollingIntervalRef) {
                        clearInterval(pollingIntervalRef);
                        setPollingIntervalRef(null);
                    }
                    
                    const resultResponse = await cocAssistantService.getJobResult(accessToken, submittedJobId);
                    setResult(resultResponse);
                    setStep(3);
                    setLoading(false);

                    // Show notification based on confidence
                    if (resultResponse.overallConfidence === 'HIGH' && resultResponse.uncertainFields.length === 0) {
                        console.log('COC processed successfully with high confidence');
                    } else {
                        console.log(`COC processed with ${resultResponse.overallConfidence} confidence. Review may be needed.`);
                    }
                } else if (statusResponse.status === 'FAILED') {
                    // Job failed
                    if (pollingIntervalRef) {
                        clearInterval(pollingIntervalRef);
                        setPollingIntervalRef(null);
                    }
                    setError(statusResponse.error || 'Processing failed');
                    setLoading(false);
                    setStep(1);
                }
                // Continue polling if PENDING or PROCESSING
            } catch (err) {
                console.error('Error polling job status:', err);
                if (pollingIntervalRef) {
                    clearInterval(pollingIntervalRef);
                    setPollingIntervalRef(null);
                }
                setError('Failed to check processing status');
                setLoading(false);
                setStep(1);
            }
        };

        // Initial poll
        await poll();
        
        // Set up interval (every 2 seconds) - only if not already completed
        if (jobStatus !== 'COMPLETED' && jobStatus !== 'FAILED') {
            const interval = setInterval(poll, 2000);
            setPollingIntervalRef(interval);
        }
    };

    /**
     * Cancel current job
     */
    const handleCancelJob = async () => {
        if (!jobId) return;

        try {
            const accessToken = await getBackendAccessToken();
            await cocAssistantService.cancelJob(accessToken, jobId);
            
            if (pollingIntervalRef) {
                clearInterval(pollingIntervalRef);
                setPollingIntervalRef(null);
            }
            
            setJobId(null);
            setJobStatus(null);
            setLoading(false);
            setStep(1);
            console.log('Job cancelled successfully');
        } catch (err) {
            console.error('Failed to cancel job:', err);
            setError('Failed to cancel processing');
        }
    };

    /**
     * Handle COC text interpretation (async)
     */
    const handleInterpret = async () => {
        if (!cocText.trim()) {
            setError('Please enter or paste COC text');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setStep(2); // Move to processing step

            const accessToken = await getBackendAccessToken();
            const submissionResponse = await cocAssistantService.submitCocText(
                accessToken,
                cocText.trim(),
                'user' // Could get actual user email/name here
            );

            setJobId(submissionResponse.jobId);
            console.log('COC text submitted for processing:', submissionResponse.jobId);

            // Register job with AlertContext for background polling
            addProcessingJob(submissionResponse.jobId, 'COC Text', 'COC_PARSING');

            // Start polling for job completion
            await startJobPolling(submissionResponse.jobId);

        } catch (err) {
            console.error('Failed to submit COC text:', err);
            setError('Failed to submit COC for processing. Please try again.');
            setLoading(false);
            setStep(1);
        }
    };

    /**
     * Handle clarifications for uncertain fields
     */
    const handleClarify = async () => {
        if (!result?.confirmationId) return;

        try {
            setLoading(true);
            setError(null);

            const accessToken = await getBackendAccessToken();
            const response = await cocAssistantService.provideClarification(accessToken, {
                confirmationId: result.confirmationId,
                clarifications
            });

            setResult(response);
            setClarifications({});
            console.log('Clarifications applied successfully');
        } catch (err) {
            console.error('Failed to apply clarifications:', err);
            setError('Failed to apply clarifications. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle file upload - supports both PDF and text files
     */
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Check file type - accept PDF or text
        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        const isText = file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt');

        if (!isPdf && !isText) {
            setError('Please upload a PDF or text file (.pdf, .txt)');
            return;
        }

        // For PDF files, submit for async processing
        if (isPdf) {
            try {
                setLoading(true);
                setError(null);
                setStep(2); // Move to processing step

                const accessToken = await getBackendAccessToken();
                const submissionResponse = await cocAssistantService.submitCocPdf(
                    accessToken,
                    file,
                    'user' // Could get actual user email/name here
                );

                setJobId(submissionResponse.jobId);
                console.log('PDF submitted for processing:', submissionResponse.jobId);

                // Register job with AlertContext for background polling
                addProcessingJob(submissionResponse.jobId, file.name, 'COC_PARSING');

                // Start polling for job completion
                await startJobPolling(submissionResponse.jobId);

            } catch (err) {
                console.error('Failed to submit PDF COC:', err);
                const errorMsg = err.message || 'Unknown error occurred';
                setError(`Failed to submit PDF: ${errorMsg}`);
                
                // Add user-friendly suggestions based on error
                if (errorMsg.includes('500') || errorMsg.includes('Internal Server Error')) {
                    setError(`Backend error processing PDF. Please check:\n• PDF file is not corrupted\n• PDF contains readable text or images\n• Backend service is running properly\n\nTechnical error: ${errorMsg}`);
                } else if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
                    setError('Authentication failed. Please log in again.');
                } else if (errorMsg.includes('413') || errorMsg.includes('too large')) {
                    setError('PDF file is too large. Please try a smaller file.');
                } else {
                    setError(`Failed to submit PDF: ${errorMsg}`);
                }
                
                setLoading(false);
                setStep(1);
            }
            return;
        }

        // For text files, read content and populate textarea
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            setCocText(text);
            setError(null);
        };
        reader.onerror = () => {
            setError('Failed to read file. Please try again.');
        };
        reader.readAsText(file);
    };

    /**
     * Get confidence badge class
     */
    const getConfidenceBadgeClass = () => {
        if (!result) return '';
        switch (result.overallConfidence) {
            case 'HIGH':
                return styles.confidenceHigh;
            case 'MEDIUM':
                return styles.confidenceMedium;
            case 'LOW':
                return styles.confidenceLow;
            default:
                return '';
        }
    };

    /**
     * Close modal handler
     */
    const handleClose = () => {
        onClose();
    };

    /**
     * Render processing step
     */
    const renderProcessingStep = () => (
        <div className={styles.processingStep}>
            <div className={styles.processingIndicator}>
                <FaSpinner className={styles.spinning} size={48} />
                <h3>Processing COC Document...</h3>
                <p>Job ID: {jobId}</p>
                {jobStatus && <p>Status: {jobStatus}</p>}
                <p className={styles.processingNote}>
                    This may take a few moments. Your COC is being analyzed by our AI system.
                </p>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    <FaExclamationTriangle />
                    {error}
                </div>
            )}

            <div className={styles.modalActions}>
                <button
                    type="button"
                    onClick={handleCancelJob}
                    className={styles.cancelButton}
                    disabled={!jobId}
                >
                    Cancel Processing
                </button>
            </div>
        </div>
    );

    /**
     * Render upload step
     */
    const renderUploadStep = () => (
        <div className={styles.uploadStep}>
            <div className={styles.uploadArea}>
                <div className={styles.uploadIcon}>
                    <FaUpload />
                </div>
                <p className={styles.uploadText}>Upload COC Document (PDF or Text) or Paste Text</p>
                <p className={styles.uploadSubtext}>PDFs are processed automatically using AI vision</p>
                <input
                    type="file"
                    accept=".pdf,.txt,application/pdf,text/plain"
                    onChange={handleFileUpload}
                    className={styles.fileInput}
                    id="coc-file-input"
                    disabled={loading}
                />
                <label htmlFor="coc-file-input" className={`${styles.uploadButton} ${loading ? styles.disabled : ''}`}>
                    {loading ? 'Processing...' : 'Choose File (PDF or TXT)'}
                </label>
                <span className={styles.uploadDivider}>or</span>
            </div>

            <div className={styles.textInputArea}>
                <label htmlFor="coc-text">Paste COC Text:</label>
                <textarea
                    id="coc-text"
                    value={cocText}
                    onChange={(e) => setCocText(e.target.value)}
                    placeholder="Paste COC text here..."
                    rows={15}
                    className={styles.cocTextArea}
                />
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    <FaExclamationTriangle />
                    {error}
                </div>
            )}

            <div className={styles.modalActions}>
                <button
                    type="button"
                    onClick={handleClose}
                    className={styles.cancelButton}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleInterpret}
                    className={styles.primaryButton}
                    disabled={loading || !cocText.trim()}
                >
                    {loading ? (
                        <>
                            <FaSpinner className={styles.spinning} />
                            Interpreting...
                        </>
                    ) : (
                        'Interpret COC'
                    )}
                </button>
            </div>
        </div>
    );

    /**
     * Render results step
     */
    const renderResultsStep = () => {
        if (!result) return null;

        return (
            <div className={styles.resultsStep}>
                {/* Confidence Badge */}
                <div className={`${styles.confidenceBadge} ${getConfidenceBadgeClass()}`}>
                    <FaCheckCircle />
                    Confidence: {result.overallConfidence}
                </div>

                {/* Warnings */}
                {result.warnings && result.warnings.length > 0 && (
                    <div className={styles.warningsSection}>
                        <h4><FaExclamationTriangle /> Warnings</h4>
                        <ul>
                            {result.warnings.map((warning, idx) => (
                                <li key={idx}>{warning}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Uncertain Fields */}
                {result.uncertainFields && result.uncertainFields.length > 0 && (
                    <div className={styles.uncertainFieldsSection}>
                        <h4>Uncertain Fields - Please Review</h4>
                        {result.uncertainFields.map((field, idx) => (
                            <div key={idx} className={styles.uncertainField}>
                                <strong>{field.fieldPath}:</strong> {field.extractedValue}
                                <br />
                                <em className={styles.fieldReason}>Reason: {field.reason}</em>
                                <br />
                                <input
                                    type="text"
                                    placeholder="Enter correct value"
                                    className={styles.clarificationInput}
                                    onChange={(e) => setClarifications({
                                        ...clarifications,
                                        [field.fieldPath]: e.target.value
                                    })}
                                    value={clarifications[field.fieldPath] || ''}
                                />
                            </div>
                        ))}
                        <button
                            onClick={handleClarify}
                            className={styles.secondaryButton}
                            disabled={loading || Object.keys(clarifications).length === 0}
                        >
                            {loading ? (
                                <>
                                    <FaSpinner className={styles.spinning} />
                                    Applying...
                                </>
                            ) : (
                                'Apply Clarifications'
                            )}
                        </button>
                    </div>
                )}

                {/* Client Match Display */}
                {result.clientMatch && (
                    <div className={styles.dataSection}>
                        <h4>Client Validation</h4>
                        <div className={`${styles.clientMatch} ${styles[`clientMatch${result.clientMatch.matchType}`]}`}>
                            {result.clientMatch.matchType === 'EXACT_MATCH' && (
                                <>
                                    <p className={styles.clientMatchTitle}>✓ Existing Client Found</p>
                                    <p><strong>Extracted:</strong> {result.clientMatch.extractedName}</p>
                                    <p><strong>Matched:</strong> {result.clientMatch.matchedClientName} (ID: {result.clientMatch.matchedClientId})</p>
                                    {result.clientMatch.matchedClientCode && (
                                        <p><strong>Code:</strong> {result.clientMatch.matchedClientCode}</p>
                                    )}
                                    <p><strong>Confidence:</strong> {(result.clientMatch.confidence * 100).toFixed(0)}%</p>
                                </>
                            )}
                            
                            {result.clientMatch.matchType === 'SIMILAR_MATCH' && (
                                <>
                                    <p className={styles.clientMatchTitle}>⚠️ Similar Client Found - Confirmation Needed</p>
                                    <p><strong>Extracted:</strong> {result.clientMatch.extractedName}</p>
                                    <p><strong>Best Match:</strong> {result.clientMatch.matchedClientName} (ID: {result.clientMatch.matchedClientId})</p>
                                    {result.clientMatch.matchedClientCode && (
                                        <p><strong>Code:</strong> {result.clientMatch.matchedClientCode}</p>
                                    )}
                                    <p><strong>Similarity:</strong> {(result.clientMatch.confidence * 100).toFixed(0)}%</p>
                                    {result.clientMatch.similarClients && result.clientMatch.similarClients.length > 1 && (
                                        <div className={styles.similarClientsList}>
                                            <p><strong>Other Similar Clients:</strong></p>
                                            <ul>
                                                {result.clientMatch.similarClients.slice(1, 4).map((client, idx) => (
                                                    <li key={idx}>
                                                        {client.clientName} ({client.clientCode}) - {(client.similarityScore * 100).toFixed(0)}% match
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <p className={styles.clientMatchSuggestion}>{result.clientMatch.suggestion}</p>
                                </>
                            )}
                            
                            {result.clientMatch.matchType === 'NO_MATCH' && (
                                <>
                                    <p className={styles.clientMatchTitle}>🆕 New Client Required</p>
                                    <p><strong>Extracted:</strong> {result.clientMatch.extractedName}</p>
                                    <p>This client was not found in the database.</p>
                                    <p className={styles.clientMatchSuggestion}>{result.clientMatch.suggestion}</p>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* JSON Output */}
                <div className={styles.jsonSection}>
                    <h4>Extracted JSON Data</h4>
                    <pre className={styles.jsonOutput}>
                        {JSON.stringify(result, null, 2)}
                    </pre>
                </div>

                {error && (
                    <div className={styles.errorMessage}>
                        <FaExclamationTriangle />
                        {error}
                    </div>
                )}

                <div className={styles.modalActions}>
                    <button
                        type="button"
                        onClick={() => setStep(1)}
                        className={styles.secondaryButton}
                    >
                        Upload Another
                    </button>
                    <button
                        type="button"
                        onClick={handleClose}
                        className={styles.primaryButton}
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={handleClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>
                        <FaUpload />
                        {step === 1 && 'Upload COC Document'}
                        {step === 2 && 'Processing COC Document'}
                        {step === 3 && 'Interpretation Results'}
                    </h2>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        aria-label="Close modal"
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {step === 1 && renderUploadStep()}
                    {step === 2 && renderProcessingStep()}
                    {step === 3 && renderResultsStep()}
                </div>
            </div>
        </div>
    );
};

export default CocUploadModal;
